import { useEffect, useMemo, useRef, useState } from 'react'

/* =========================================================================
   PhoneField — country selector plus national number.

   Typing a country code by hand is where phone numbers go wrong: people omit
   it, guess it, or write 0091 / 91 / +91 for the same thing. Picking the
   country from a list makes the dial code a fact rather than a guess, and the
   field emits one canonical E.164 string.

   On flags: emoji flags do not render on Windows, they fall back to the two
   letter pair, so this uses flag images and falls back to an ISO chip if they
   cannot be fetched. Verified on this machine before choosing.
   ========================================================================= */

/* ISO 3166-1 alpha-2, name, dial code. Ordered with the markets this business
   actually serves at the top, then the rest alphabetically. */
export const COUNTRIES = [
  { iso: 'in', name: 'India', dial: '91' },
  { iso: 'ae', name: 'United Arab Emirates', dial: '971' },
  { iso: 'sa', name: 'Saudi Arabia', dial: '966' },
  { iso: 'qa', name: 'Qatar', dial: '974' },
  { iso: 'kw', name: 'Kuwait', dial: '965' },
  { iso: 'bh', name: 'Bahrain', dial: '973' },
  { iso: 'om', name: 'Oman', dial: '968' },
  { iso: 'sg', name: 'Singapore', dial: '65' },
  { iso: 'my', name: 'Malaysia', dial: '60' },
  { iso: 'id', name: 'Indonesia', dial: '62' },
  { iso: 'th', name: 'Thailand', dial: '66' },
  { iso: 'ph', name: 'Philippines', dial: '63' },
  { iso: 'vn', name: 'Vietnam', dial: '84' },
  { iso: 'gb', name: 'United Kingdom', dial: '44' },
  { iso: 'us', name: 'United States', dial: '1' },
  { iso: 'ca', name: 'Canada', dial: '1' },
  { iso: 'au', name: 'Australia', dial: '61' },
  { iso: 'nz', name: 'New Zealand', dial: '64' },
  { iso: 'de', name: 'Germany', dial: '49' },
  { iso: 'fr', name: 'France', dial: '33' },
  { iso: 'nl', name: 'Netherlands', dial: '31' },
  { iso: 'es', name: 'Spain', dial: '34' },
  { iso: 'it', name: 'Italy', dial: '39' },
  { iso: 'ie', name: 'Ireland', dial: '353' },
  { iso: 'ch', name: 'Switzerland', dial: '41' },
  { iso: 'se', name: 'Sweden', dial: '46' },
  { iso: 'no', name: 'Norway', dial: '47' },
  { iso: 'dk', name: 'Denmark', dial: '45' },
  { iso: 'fi', name: 'Finland', dial: '358' },
  { iso: 'be', name: 'Belgium', dial: '32' },
  { iso: 'at', name: 'Austria', dial: '43' },
  { iso: 'pl', name: 'Poland', dial: '48' },
  { iso: 'pt', name: 'Portugal', dial: '351' },
  { iso: 'gr', name: 'Greece', dial: '30' },
  { iso: 'cz', name: 'Czechia', dial: '420' },
  { iso: 'ro', name: 'Romania', dial: '40' },
  { iso: 'hu', name: 'Hungary', dial: '36' },
  { iso: 'ua', name: 'Ukraine', dial: '380' },
  { iso: 'tr', name: 'Türkiye', dial: '90' },
  { iso: 'il', name: 'Israel', dial: '972' },
  { iso: 'eg', name: 'Egypt', dial: '20' },
  { iso: 'za', name: 'South Africa', dial: '27' },
  { iso: 'ng', name: 'Nigeria', dial: '234' },
  { iso: 'ke', name: 'Kenya', dial: '254' },
  { iso: 'gh', name: 'Ghana', dial: '233' },
  { iso: 'tz', name: 'Tanzania', dial: '255' },
  { iso: 'ma', name: 'Morocco', dial: '212' },
  { iso: 'jp', name: 'Japan', dial: '81' },
  { iso: 'kr', name: 'South Korea', dial: '82' },
  { iso: 'cn', name: 'China', dial: '86' },
  { iso: 'hk', name: 'Hong Kong', dial: '852' },
  { iso: 'tw', name: 'Taiwan', dial: '886' },
  { iso: 'bd', name: 'Bangladesh', dial: '880' },
  { iso: 'pk', name: 'Pakistan', dial: '92' },
  { iso: 'lk', name: 'Sri Lanka', dial: '94' },
  { iso: 'np', name: 'Nepal', dial: '977' },
  { iso: 'bt', name: 'Bhutan', dial: '975' },
  { iso: 'mv', name: 'Maldives', dial: '960' },
  { iso: 'br', name: 'Brazil', dial: '55' },
  { iso: 'mx', name: 'Mexico', dial: '52' },
  { iso: 'ar', name: 'Argentina', dial: '54' },
  { iso: 'cl', name: 'Chile', dial: '56' },
  { iso: 'co', name: 'Colombia', dial: '57' },
  { iso: 'pe', name: 'Peru', dial: '51' },
  { iso: 'ru', name: 'Russia', dial: '7' },
  { iso: 'jo', name: 'Jordan', dial: '962' },
  { iso: 'lb', name: 'Lebanon', dial: '961' },
  { iso: 'iq', name: 'Iraq', dial: '964' },
  { iso: 'af', name: 'Afghanistan', dial: '93' },
  { iso: 'mm', name: 'Myanmar', dial: '95' },
  { iso: 'kh', name: 'Cambodia', dial: '855' },
  { iso: 'mu', name: 'Mauritius', dial: '230' },
  { iso: 'et', name: 'Ethiopia', dial: '251' },
  { iso: 'ug', name: 'Uganda', dial: '256' },
  { iso: 'rw', name: 'Rwanda', dial: '250' },
  { iso: 'zm', name: 'Zambia', dial: '260' },
]

const DEFAULT_ISO = 'in'

/* Longest dial code first, so +1 does not shadow +91 when splitting a stored
   number back into country and national parts. */
const BY_DIAL_DESC = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)

/** Split a stored E.164 string into a country and the national remainder. */
export function splitE164(value) {
  const raw = String(value || '').replace(/[^\d+]/g, '')
  if (!raw.startsWith('+')) {
    return { country: COUNTRIES.find((c) => c.iso === DEFAULT_ISO), national: raw.replace(/\D/g, '') }
  }
  const digits = raw.slice(1)
  const hit = BY_DIAL_DESC.find((c) => digits.startsWith(c.dial))
  return hit
    ? { country: hit, national: digits.slice(hit.dial.length) }
    : { country: COUNTRIES.find((c) => c.iso === DEFAULT_ISO), national: digits }
}

function Flag({ iso, className = '' }) {
  const [failed, setFailed] = useState(false)
  if (failed) {
    return (
      <span className={`grid h-4 w-6 shrink-0 place-items-center rounded-[2px] bg-slate-200 font-mono text-[8px] font-bold uppercase text-slate-600 ${className}`}>
        {iso}
      </span>
    )
  }
  return (
    <img
      /* Served from this site, not flagcdn. A flag that fails to load because
         someone else's CDN is unreachable drops the field back to an ISO
         badge, which looks like a bug in the form rather than a network
         problem somewhere else. */
      src={`/images/flags/w40-${iso}.png`}
      srcSet={`/images/flags/w80-${iso}.png 2x`}
      width={24}
      height={16}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={`h-4 w-6 shrink-0 rounded-[2px] object-cover ring-1 ring-black/10 ${className}`}
    />
  )
}

/**
 * Controlled. `value` is the full E.164 string, `onChange` receives the same.
 * `tone` picks the palette so the field can sit on a light form or a dark one.
 */
export default function PhoneField({
  value = '',
  onChange,
  placeholder = 'Phone number',
  name,
  required = false,
  tone = 'light',
  className = '',
  ariaLabel,
}) {
  const initial = useMemo(() => splitE164(value), [])
  const [country, setCountry] = useState(initial.country)
  const [national, setNational] = useState(initial.national)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef(null)
  const searchRef = useRef(null)

  // Re-sync if a parent resets the field (a cleared form, a loaded record).
  useEffect(() => {
    const next = splitE164(value)
    if (`+${country.dial}${national}` !== String(value || '')) {
      setCountry(next.country)
      setNational(next.national)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const emit = (c, n) => onChange?.(n ? `+${c.dial}${n}` : '')

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    const t = setTimeout(() => searchRef.current?.focus(), 30)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
      clearTimeout(t)
    }
  }, [open])

  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return COUNTRIES
    /* The digit clause is only applied when the query actually contains
       digits: every string startsWith('') is true, so an empty digit
       extraction would match the entire list on a plain name search. */
    const digits = q.replace(/\D/g, '')
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso === q ||
        (digits !== '' && c.dial.startsWith(digits))
    )
  }, [query])

  const dark = tone === 'dark'
  const shell = dark
    ? 'border-slate-700/60 bg-[#0F172A] focus-within:border-[#06B6D4] focus-within:ring-1 focus-within:ring-[#06B6D4]'
    : 'border-slate-200 bg-white focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-600/15'
  const text = dark ? 'text-white placeholder-slate-400' : 'text-slate-900 placeholder:text-slate-400'
  const divider = dark ? 'border-slate-700/60' : 'border-slate-200'

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className={`flex items-stretch overflow-hidden rounded-[16px] border transition-all ${shell}`}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Country code, currently ${country.name} +${country.dial}`}
          className={`flex shrink-0 items-center gap-2 border-r px-3.5 ${divider} ${dark ? 'text-slate-200 hover:bg-white/5' : 'text-slate-700 hover:bg-slate-50'} transition-colors`}
        >
          <Flag iso={country.iso} />
          <span className="font-sans text-[13px] font-semibold tabular-nums">+{country.dial}</span>
          <svg viewBox="0 0 24 24" className="size-3 opacity-60" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        <input
          type="tel"
          name={name}
          required={required}
          inputMode="tel"
          autoComplete="tel-national"
          aria-label={ariaLabel || placeholder}
          value={national}
          onChange={(e) => {
            const raw = e.target.value

            /* Somebody pasting a full international number would otherwise
               end up with the dial code twice, because the selector already
               contributes it. Anything that looks international is re-parsed
               and the country selector follows it. */
            if (/^\s*(\+|00)/.test(raw)) {
              const normalised = raw.trim().replace(/^00/, '+')
              const parsed = splitE164(normalised)
              setCountry(parsed.country)
              setNational(parsed.national)
              emit(parsed.country, parsed.national)
              return
            }

            const n = raw.replace(/\D/g, '')
            setNational(n)
            emit(country, n)
          }}
          placeholder={placeholder}
          className={`w-full bg-transparent px-4 py-3.5 text-[16px] outline-none sm:text-sm ${text}`}
        />
      </div>

      {open && (
        <div
          role="listbox"
          className="absolute z-50 mt-2 max-h-72 w-full min-w-[280px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        >
          <div className="border-b border-slate-100 p-2">
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code"
              aria-label="Search country"
              className="w-full rounded-xl bg-slate-50 px-3 py-2 text-[13px] text-slate-900 outline-none placeholder:text-slate-400"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {results.length === 0 && (
              <li className="px-4 py-6 text-center text-[13px] text-slate-500">No country matches that.</li>
            )}
            {results.map((c) => (
              <li key={`${c.iso}-${c.dial}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.iso === country.iso}
                  onClick={() => {
                    setCountry(c)
                    emit(c, national)
                    setOpen(false)
                    setQuery('')
                  }}
                  className={
                    'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ' +
                    (c.iso === country.iso ? 'bg-blue-50' : 'hover:bg-slate-50')
                  }
                >
                  <Flag iso={c.iso} />
                  <span className="min-w-0 flex-1 truncate text-[13.5px] text-slate-800">{c.name}</span>
                  <span className="font-sans text-[12.5px] font-semibold tabular-nums text-slate-500">+{c.dial}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
